import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Cadence for {@link useServiceStatuses}. The endpoint is one exec on the
 * devbox (a sweep of the on-box status/pid files), so 10s keeps a died daemon
 * from wearing a green dot for long without hammering the box. Callers gate
 * `enabled` on the surface actually being visible (detail panel section
 * mounted, header popover open).
 */
const SERVICE_STATUS_POLL_MS = 10_000;

/**
 * Poll the live status of the services on an agent's devbox while a servers
 * surface is in view.
 *
 * The persisted `agent.daemons` list only records that a daemon once reported
 * itself started — a process that has since crashed keeps its entry. This
 * hook reads the real on-box state (persisted lifecycle status plus a pid
 * liveness probe) so the server list can show whether each daemon is actually
 * running and gate its log link on a real service id. Polling pauses while
 * the tab is backgrounded.
 */
export function useServiceStatuses(
  agentId: string | null | undefined,
  options?: { enabled?: boolean; intervalMs?: number },
) {
  const enabled = (options?.enabled ?? true) && Boolean(agentId);
  return useQuery({
    queryKey: queryKeys.serviceStatuses(agentId ?? 'none'),
    queryFn: () => api.getServiceStatuses(agentId!),
    enabled,
    refetchInterval: enabled ? (options?.intervalMs ?? SERVICE_STATUS_POLL_MS) : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

/**
 * Fetch one service's on-box log file for the log viewer dialog. Enabled only
 * while a service is selected; refetched on demand via the dialog's refresh
 * (React Query `refetch`) rather than polled.
 */
export function useServiceLog(
  agentId: string | null | undefined,
  serviceId: string | null | undefined,
) {
  return useQuery({
    queryKey: queryKeys.serviceLog(agentId ?? 'none', serviceId ?? 'none'),
    queryFn: () => api.getServiceLog(agentId!, serviceId!),
    enabled: Boolean(agentId) && Boolean(serviceId),
    staleTime: 0,
  });
}
